
/* eslint-disable react-refresh/only-export-components -- provider and its typed hook intentionally share this module */
import React, { createContext, useCallback, useContext, useMemo } from "react";
import { phpApi } from "@/integrations/php/client";

interface AssignmentContextType {
  getNextPartnerIdForAssignment: (serviceType: string) => Promise<string | null>;
}

const AssignmentContext = createContext<AssignmentContextType | undefined>(undefined);

export function useAssignment() {
  const context = useContext(AssignmentContext);
  if (context === undefined) {
    throw new Error("useAssignment must be used within an AssignmentProvider");
  }
  return context;
}

export const AssignmentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Round Robin assignment algorithm
  const getNextPartnerIdForAssignment = useCallback(async (serviceType: string): Promise<string | null> => {
    try {
      // Fetch active partners that offer this service
      const { data: partners, error } = await phpApi
        .from('partners')
        .select('*')
        .eq('active', true)
        .contains('services', [serviceType])
        .order('created_at', { ascending: true });
      
      if (error || !partners || partners.length === 0) {
        console.error("Error fetching partners or no partners found:", error);
        return null;
      }
      
      // Fetch recent lead assignments to determine which partner should be next
      const { data: recentAssignments, error: recentError } = await phpApi
        .from('leads')
        .select('partner_id, created_at')
        .not('partner_id', 'is', null)
        .order('created_at', { ascending: false })
        .limit(partners.length * 2);
        
      if (recentError) {
        console.error("Error fetching recent assignments:", recentError);
        // If there's an error, just use the first partner
        return partners[0].id;
      }
      
      // If no recent assignments, assign to the first partner
      if (!recentAssignments || recentAssignments.length === 0) {
        return partners[0].id;
      }
      
      // Create a map of partner IDs for quick lookup.
      // Typed explicitly: inferring from the tuple widened the key to
      // `string | Partner`, so `keys()` produced values TypeScript could not
      // accept as the string return type below.
      const partnerIdMap = new Map<string, (typeof partners)[number]>(
        partners.map(p => [p.id, p])
      );
      
      // Find partners who haven't been assigned recently
      const recentlyAssignedIds = new Set(
        recentAssignments
          .filter(a => a.partner_id) // Filter out null partner_ids
          .map(a => a.partner_id)
      );
      
      // First, try to find partners who haven't been assigned at all
      const unassignedPartners = partners.filter(p => !recentlyAssignedIds.has(p.id));
      if (unassignedPartners.length > 0) {
        return unassignedPartners[0].id;
      }
      
      // If all partners have been assigned, find the one that was assigned least recently
      const assignmentOrder = recentAssignments
        .filter(a => partnerIdMap.has(a.partner_id)) // Only consider active partners
        .map(a => a.partner_id);
      
      // Find the first partner that appears latest in the recent assignments
      for (const partnerId of [...partnerIdMap.keys()]) {
        const lastIndex = assignmentOrder.lastIndexOf(partnerId);
        if (lastIndex === -1 || lastIndex === assignmentOrder.length - 1) {
          continue; // Skip if not found or most recently assigned
        }
        return partnerId;
      }
      
      // If we couldn't determine, pick the first partner
      return partners[0].id;
    } catch (error) {
      console.error("Error in round robin assignment:", error);
      return null;
    }
  }, []);

  const value = useMemo(() => ({ getNextPartnerIdForAssignment }), [getNextPartnerIdForAssignment]);

  return (
    <AssignmentContext.Provider value={value}>
      {children}
    </AssignmentContext.Provider>
  );
};
